import { type DayClose, kstDateKey } from "@sobidogam/domain";
import { useEffect, useState } from "react";

/**
 * Retention signals are computed on the device and only coarse buckets leave
 * it. The server never receives a device identifier (complete-services 10절).
 */
const FIRST_SEEN_KEY = "sobidogam:firstSeenOn";
const FIRST_RECORD_KEY = "sobidogam:firstRecordOn";
const FIRST_WEEK_SENT_KEY = "sobidogam:firstWeekSent";
const CAPTURE_STARTED_KEY = "sobidogam:captureStartedAt";

function safeGet(key: string): string | null {
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

function safeSet(key: string, value: string) {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// Private mode: signals are best effort and never block recording.
	}
}

function daysSince(dateKey: string, now: Date): number {
	const start = Date.parse(`${dateKey}T00:00:00+09:00`);
	const today = Date.parse(`${kstDateKey(now)}T00:00:00+09:00`);
	if (!Number.isFinite(start) || !Number.isFinite(today)) return 0;
	return Math.max(0, Math.round((today - start) / 86_400_000));
}

export function daysSinceFirstBucket(now = new Date()): string {
	let first = safeGet(FIRST_SEEN_KEY);
	if (!first) {
		first = kstDateKey(now);
		safeSet(FIRST_SEEN_KEY, first);
	}
	const days = daysSince(first, now);
	if (days === 0) return "0";
	if (days <= 6) return "1-6";
	if (days <= 13) return "7-13";
	return "14+";
}

export function markCaptureStarted() {
	if (!safeGet(CAPTURE_STARTED_KEY))
		safeSet(CAPTURE_STARTED_KEY, String(Date.now()));
}

/** Returns the first-record event props once per device, otherwise null. */
export function firstRecordSignal(
	mode: string,
	now = new Date(),
): { mode: string; elapsedBucket: string } | null {
	if (safeGet(FIRST_RECORD_KEY)) return null;
	safeSet(FIRST_RECORD_KEY, kstDateKey(now));
	const started = Number(safeGet(CAPTURE_STARTED_KEY));
	const elapsed =
		Number.isFinite(started) && started > 0 ? now.getTime() - started : 0;
	return {
		mode,
		elapsedBucket:
			elapsed < 30_000 ? "<30s" : elapsed < 60_000 ? "30-60s" : "60s+",
	};
}

/**
 * Core metric: records saved within 7 days after the first record. Sent once,
 * on the first launch at least 8 days after the first record.
 */
export function firstWeekSummary(
	receipts: {
		transactedAt: string;
		createdAt: string;
		deletedAt: string | null;
	}[],
	now = new Date(),
	closes: DayClose[] = [],
): { recordsBucket: string; closesBucket: string } | null {
	const first = safeGet(FIRST_RECORD_KEY);
	if (!first || safeGet(FIRST_WEEK_SENT_KEY)) return null;
	if (daysSince(first, now) < 8) return null;
	const start = Date.parse(`${first}T00:00:00+09:00`);
	const end = start + 7 * 86_400_000;
	const count = receipts.filter((receipt) => {
		const created = Date.parse(receipt.createdAt);
		return !receipt.deletedAt && created >= start && created < end;
	}).length;
	safeSet(FIRST_WEEK_SENT_KEY, "true");
	const closed = closes.filter((c) => {
		const at = Date.parse(`${c.date}T00:00:00+09:00`);
		return at >= start && at < end;
	}).length;
	return {
		recordsBucket: count >= 3 ? "3+" : count >= 1 ? "1-2" : "0",
		closesBucket: closed >= 3 ? "3+" : closed >= 1 ? "1-2" : "0",
	};
}

export interface ServerFeatures {
	receiptAi: boolean;
	serverBackup: boolean;
	known: boolean;
}

const OFF: ServerFeatures = {
	receiptAi: false,
	serverBackup: false,
	known: false,
};

let cached: Promise<ServerFeatures> | null = null;

/** Missing or failed health answers count as "off" so nothing is uploaded. */
export function loadServerFeatures(
	apiBaseUrl: string | undefined,
): Promise<ServerFeatures> {
	if (!apiBaseUrl) return Promise.resolve(OFF);
	cached ??= fetch(`${apiBaseUrl.replace(/\/$/, "")}/api/v1/health`)
		.then((response) => (response.ok ? response.json() : null))
		.then((body: { data?: { features?: Partial<ServerFeatures> } } | null) => ({
			receiptAi: body?.data?.features?.receiptAi === true,
			serverBackup: body?.data?.features?.serverBackup === true,
			known: Boolean(body?.data?.features),
		}))
		.catch(() => OFF);
	return cached;
}

export function useServerFeatures(apiBaseUrl: string | undefined) {
	const [features, setFeatures] = useState<ServerFeatures>(OFF);
	useEffect(() => {
		let active = true;
		void loadServerFeatures(apiBaseUrl).then((value) => {
			if (active) setFeatures(value);
		});
		return () => {
			active = false;
		};
	}, [apiBaseUrl]);
	return features;
}
